import { buildUrl, usKidsGolfService } from '@/libs/api/instances/us-kids-golf-instance';
import type { RequestOptions } from '@/libs/api/types';
import type { ActivityEventType } from './push-coach-activity';

/** Path (relative to `buildUrl`) for the family activity feed. */
export const FAMILY_ACTIVITIES_ENDPOINT = '/guardians/me/activities';

/** Who performed the activity, as of the event (mirrors `ActivityActorDto`). */
export type FamilyActivityActor = {
  /** Actor display name as of the event. */
  name: string;
  /** Actor avatar URL, or null when none. */
  avatarUrl: string | null;
  /** True when the caller is the actor (render as "You"); always false for coach/system. */
  isSelf: boolean;
};

/** Subject child of the activity (mirrors `ActivityPlayerDto`). */
export type FamilyActivityPlayer = {
  id: string;
  name: string;
};

/** One item in the family activity feed (mirrors `ActivityResponseDto`). */
export type FamilyActivity = {
  id: string;
  type: ActivityEventType;
  actor: FamilyActivityActor;
  /** Subject child, or null when the activity is not tied to one. */
  player: FamilyActivityPlayer | null;
  /** Type-specific template values (excludes actor + kid_name). */
  params: Record<string, string>;
  /** Server-rendered description from the type template. */
  description: string;
  /** When it happened (ISO-8601). */
  occurredAt: string;
};

/** Query params for the family activity feed (mirrors the endpoint's query). */
export type FamilyActivitiesParams = {
  /** Page size when `paginate=true` (1–50, ignored otherwise). */
  limit?: number;
  /** Opaque cursor from a previous page; omit for the first page. */
  cursor?: string;
  /** When true, page the results; default (false) returns all activities. */
  paginate?: boolean;
};

/** Page of the family activity feed (mirrors `FamilyActivitiesPageResponseDto`). */
export type FamilyActivitiesPage = {
  activities: FamilyActivity[];
  /** Cursor for the next page; null on the last page. */
  nextCursor: string | null;
};

/**
 * Fetches the signed-in guardian's family activity feed (newest first) via
 * GET `/api/v1/guardians/me/activities`. Authenticated with the Firebase bearer
 * token. Without `paginate`, returns every activity in one page.
 */
export async function fetchFamilyActivities(
  params?: FamilyActivitiesParams,
  options?: RequestOptions,
): Promise<FamilyActivitiesPage> {
  return await usKidsGolfService.get<FamilyActivitiesPage>(
    buildUrl(FAMILY_ACTIVITIES_ENDPOINT),
    params,
    options,
  );
}
